import { logInfo } from '../../config_and_helpers/logger';
import { findUserOption } from '../../config_and_helpers/userDropdownOptions';

const LOG_CONTEXT = 'StockItemAssociatedUser';

/**
 * Display string for a user object or numeric id (same rules as getAssociatedUserDisplay).
 *
 * @param {Object|number|null|undefined} user
 * @returns {string|null}
 */
function userDisplayName(user) {
  if (user == null) {
    return null;
  }
  if (typeof user === 'number') {
    return `User #${user}`;
  }
  if (user.first_name != null && user.last_name != null) {
    return `${user.first_name} ${user.last_name}`.trim();
  }
  if (user.username) {
    return user.username;
  }
  return user.id != null ? `User #${user.id}` : null;
}

/**
 * Order-associated user label (order.user, else quote.user).
 *
 * @param {Object|null|undefined} order
 * @returns {string|null}
 */
export function orderAssociatedUserDisplay(order) {
  return userDisplayName(order?.user ?? order?.quote?.user);
}

/**
 * User id from a field that may be a nested user object, a raw id, or empty.
 *
 * @param {Object|number|string|null|undefined} user
 * @returns {number|string|null}
 */
export function nestedUserId(user) {
  if (user == null || user === '') {
    return null;
  }
  if (typeof user === 'object') {
    return user.id ?? null;
  }
  return user;
}

/**
 * Id to preselect on the stock-item **User** (associated) picker.
 * Prefers the stock item's `associated_user`, then the related order's user
 * as a default only — independent of Opened By.
 *
 * @param {Object|null|undefined} itemObj
 * @returns {number|string|null}
 */
export function resolveStockItemAssociatedUserId(itemObj) {
  const fromItem = nestedUserId(itemObj?.associated_user);
  if (fromItem != null) {
    return fromItem;
  }
  if (itemObj?.associated_user_id != null && itemObj.associated_user_id !== '') {
    return itemObj.associated_user_id;
  }
  return nestedUserId(itemObj?.order?.user ?? itemObj?.order?.quote?.user);
}

/**
 * Resolve the dropdown option matching a stored Opened By name (label).
 *
 * @param {Array<{ value: *, label: string }>} userOptions
 * @param {string} [openedBy]
 * @returns {{ value: *, label: string }|null}
 */
export function findOpenedByUserOption(userOptions, openedBy = '') {
  const name = String(openedBy || '').trim();
  if (!name) {
    return null;
  }
  const list = Array.isArray(userOptions) ? userOptions : [];
  return (
    list.find(
      o =>
        o.value !== '' &&
        String(o.label || '')
          .trim()
          .toLowerCase() === name.toLowerCase()
    ) ?? null
  );
}

/**
 * View-mode label for the User column (associated user only — not Opened By).
 *
 * @param {Object} args
 * @param {Object|null|undefined} args.itemObj
 * @param {Array<{ value: *, label: string }>} args.userOptions
 * @param {number|string|null|undefined} args.associatedUserId
 * @returns {string}
 */
export function stockItemUserViewLabel({
  itemObj,
  userOptions,
  associatedUserId,
}) {
  const uid = associatedUserId ?? resolveStockItemAssociatedUserId(itemObj);
  if (uid != null && uid !== '') {
    const fromList = findUserOption(userOptions, uid)?.label;
    if (fromList) {
      return fromList;
    }
  }
  if (itemObj?.associated_user && typeof itemObj.associated_user === 'object') {
    const nested = userDisplayName(itemObj.associated_user);
    if (nested) {
      return nested;
    }
  }
  return orderAssociatedUserDisplay(itemObj?.order) ?? 'N/A';
}

/**
 * View-mode label for Opened By — stored name only (independent of User).
 *
 * @param {Object} args
 * @param {string} [args.openedBy]
 * @returns {string}
 */
export function stockItemOpenedByViewLabel({ openedBy = '' }) {
  const name = String(openedBy || '').trim();
  return name || 'N/A';
}

/**
 * Logs a stock-item associated-user change (User column).
 *
 * @param {Object} extra
 */
export function logStockItemAssociatedUserChange(extra = {}) {
  logInfo(LOG_CONTEXT, 'associated user changed', extra);
}

/**
 * Logs a stock-item Opened By change (independent of associated user).
 *
 * @param {Object} extra
 */
export function logStockItemOpenedByChange(extra = {}) {
  logInfo(LOG_CONTEXT, 'opened by changed', extra);
}
